/**
 * Dispute hooks — TanStack Query wrappers around disputeService.
 *
 * `useDispute(id)` subscribes to realtime UPDATEs on rental_disputes
 * for the given id so the detail screen reflects status changes
 * (escalation, resolution) without a manual pull-to-refresh.
 */
import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { disputeService } from '@/lib/services/dispute.service'
import { useAuth } from '@/lib/hooks/use-auth'
import type {
  FileDisputeInput,
  RentalDispute,
  RespondToDisputeInput,
} from '@/lib/types/dispute.types'

const disputeKeys = {
  all: ['disputes'] as const,
  list: (userId: string) => [...disputeKeys.all, 'list', userId] as const,
  byRental: (rentalId: string) => [...disputeKeys.all, 'rental', rentalId] as const,
  detail: (disputeId: string) => [...disputeKeys.all, 'detail', disputeId] as const,
}

export function useDispute(disputeId: string | null | undefined) {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: disputeKeys.detail(disputeId ?? ''),
    queryFn: () => disputeService.getDispute(disputeId!),
    enabled: !!disputeId,
    staleTime: 30 * 1000,
  })

  // Realtime: refetch on UPDATE so escalation cron + resolution events
  // surface without pull-to-refresh. RLS gates the subscription to the
  // two parties + admin, so we don't need a server-side filter check
  // beyond the eq filter.
  useEffect(() => {
    if (!disputeId) return
    const channel = supabase
      .channel(`dispute:${disputeId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rental_disputes',
          filter: `id=eq.${disputeId}`,
        },
        () => {
          void qc.invalidateQueries({ queryKey: disputeKeys.detail(disputeId) })
        },
      )
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [disputeId, qc])

  return query
}

/**
 * All disputes attached to a single rental, ordered most-recent first.
 * Used by the trip detail "Report damage" CTA to decide whether to
 * show "File new dispute" vs "View existing dispute".
 */
export function useTripDisputes(rentalId: string | null | undefined) {
  return useQuery<RentalDispute[]>({
    queryKey: disputeKeys.byRental(rentalId ?? ''),
    queryFn: () => disputeService.listForRental(rentalId!),
    enabled: !!rentalId,
    staleTime: 30 * 1000,
  })
}

export function useFileDispute() {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: FileDisputeInput) => {
      if (!user?.id) throw new Error('Not authenticated')
      return disputeService.fileDispute(user.id, input)
    },
    onSuccess: () => {
      if (user?.id) {
        void qc.invalidateQueries({ queryKey: disputeKeys.list(user.id) })
      }
    },
  })
}

export function useRespondToDispute() {
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RespondToDisputeInput) => {
      if (!user?.id) throw new Error('Not authenticated')
      return disputeService.respondToDispute(user.id, input)
    },
    onSuccess: (result) => {
      if (user?.id) {
        void qc.invalidateQueries({ queryKey: disputeKeys.list(user.id) })
      }
      if (result?.id) {
        void qc.invalidateQueries({ queryKey: disputeKeys.detail(result.id) })
      }
    },
  })
}
